import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useUserRoles } from '@/hooks/useUserRoles';

interface AdminCustomization {
  app_name: string | null;
  logo_url: string | null;
  font_family: string | null;
  primary_color: string | null;
  whistleblower_url: string | null;
}

export const useTargetAdminCustomization = (targetAdminId?: string) => {
  const [customization, setCustomization] = useState<AdminCustomization | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { isSuperAdmin } = useUserRoles();

  const fetchCustomization = async () => {
    if (!targetAdminId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('📱 Fetching customization for admin:', targetAdminId);

      const { data, error } = await supabase
        .from('admin_customizations')
        .select('*')
        .eq('admin_id', targetAdminId)
        .single();

      console.log('📱 Response:', { data, error });

      if (error && error.code !== 'PGRST116') {
        console.error('❌ Error fetching admin customization:', error);
        setCustomization(null);
        return;
      }

      if (data) {
        console.log('✅ Fetched customization:', data);
        setCustomization({
          app_name: data.app_name,
          logo_url: data.logo_url,
          font_family: data.font_family,
          primary_color: data.primary_color,
          whistleblower_url: data.whistleblower_url,
        });
      } else {
        console.log('📱 No customization found for admin');
        setCustomization(null);
      }
    } catch (error) {
      console.error('❌ Exception fetching admin customization:', error);
      setCustomization(null);
    } finally {
      setLoading(false);
    }
  };

  const saveWhistleblowerUrl = async (whistleblowerUrl: string): Promise<boolean> => {
    if (!targetAdminId) {
      toast({
        title: "Error",
        description: "No admin ID provided",
        variant: "destructive",
      });
      return false;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Only super admins can update whistleblower URLs
      if (!isSuperAdmin) {
        throw new Error('Only super admins can update whistleblower URLs');
      }

      // First, check if customization exists
      const { data: existing } = await supabase
        .from('admin_customizations')
        .select('*')
        .eq('admin_id', targetAdminId)
        .single();

      const updateData: any = {
        admin_id: targetAdminId,
        whistleblower_url: whistleblowerUrl,
        // Preserve existing values or use defaults
        app_name: existing?.app_name || 'Shiftask',
        logo_url: existing?.logo_url,
        font_family: existing?.font_family || 'inter',
        primary_color: existing?.primary_color || '221 83% 53%',
      };

      const { error } = await supabase
        .from('admin_customizations')
        .upsert(updateData, {
          onConflict: 'admin_id'
        });

      if (error) throw error;

      // Update local state
      setCustomization({
        ...customization,
        whistleblower_url: whistleblowerUrl,
      });

      toast({
        title: "Success",
        description: "Whistleblower URL saved successfully",
      });

      return true;
    } catch (error: any) {
      console.error('Error saving whistleblower URL:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to save whistleblower URL",
        variant: "destructive",
      });
      return false;
    }
  };

  useEffect(() => {
    fetchCustomization();
  }, [targetAdminId]);

  return {
    customization,
    loading,
    saveWhistleblowerUrl,
    refetch: fetchCustomization,
  };
};
